import {useState} from 'react';
import useClipboard from 'react-use-clipboard';
import {AccountSettingsPanel} from '../account-settings-panel';
import {AccountSettingsId} from '../account-settings-sidenav';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {ConfirmationDialog} from '@ui/overlays/dialog/confirmation-dialog';
import {ContentCopyIcon} from '@ui/icons/material/ContentCopy';
import {
  useTelegramLinkStatus,
  useTelegramLinkToken,
  useTelegramUnlink,
} from './telegram-requests';

interface TokenData {
  token: string;
  expires_at: string;
}

export function TelegramPanel() {
  const {data, isLoading, refetch} = useTelegramLinkStatus();
  const createToken = useTelegramLinkToken();
  const unlink = useTelegramUnlink();
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const command = tokenData ? `/link ${tokenData.token}` : '';
  const [isCopied, copyToClipboard] = useClipboard(command, {
    successDuration: 1200,
  });

  const isLinked = !!data?.linked;

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Telegram}
      title={<Trans message="Telegram bot" />}
      actions={
        isLinked ? (
          <DialogTrigger
            type="modal"
            onClose={isConfirmed => {
              if (isConfirmed) {
                unlink.mutate(undefined, {
                  onSuccess: () => {
                    setTokenData(null);
                    refetch();
                  },
                });
              }
            }}
          >
            <Button
              variant="outline"
              color="danger"
              size="xs"
              disabled={unlink.isPending}
            >
              <Trans message="Unlink" />
            </Button>
            <ConfirmationDialog
              isDanger
              title={<Trans message="Unlink Telegram?" />}
              body={
                <Trans message="You will no longer be able to use Telegram for uploads and file management until you link again." />
              }
              confirm={<Trans message="Unlink" />}
            />
          </DialogTrigger>
        ) : undefined
      }
    >
      {isLoading || !data ? (
        <div className="text-sm text-muted">
          <Trans message="Loading Telegram status..." />
        </div>
      ) : (
        <div className="space-y-12 text-sm">
          <div>
            <span className="font-semibold">
              <Trans message="Status:" />
            </span>{' '}
            {isLinked ? (
              <Trans message="Linked" />
            ) : (
              <Trans message="Not linked" />
            )}
          </div>
          {isLinked && data.telegram_id ? (
            <div>
              <div>
                <span className="font-semibold">
                  <Trans message="Telegram ID:" />
                </span>{' '}
                {data.telegram_id}
              </div>
              {data.telegram_linked_at ? (
                <div>
                  <span className="font-semibold">
                    <Trans message="Linked at:" />
                  </span>{' '}
                  <FormattedDate date={data.telegram_linked_at} />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="pt-6">
            <Button
              variant="flat"
              color="primary"
              size="xs"
              disabled={createToken.isPending}
              onClick={() => {
                createToken.mutate(undefined, {
                  onSuccess: response => {
                    setTokenData({
                      token: response.token,
                      expires_at: response.expires_at,
                    });
                  },
                });
              }}
            >
              <Trans
                message={tokenData ? 'Regenerate link token' : 'Generate link token'}
              />
            </Button>
          </div>

          {tokenData ? (
            <div className="rounded border bg p-12">
              <div className="mb-6 font-semibold">
                <Trans message="Send this command to the bot:" />
              </div>
              <TextField
                readOnly
                value={command}
                onClick={e => {
                  e.currentTarget.focus();
                  e.currentTarget.select();
                }}
                endAppend={
                  <Button
                    variant="flat"
                    color="primary"
                    size="xs"
                    onClick={copyToClipboard}
                    startIcon={<ContentCopyIcon />}
                  >
                    {isCopied ? (
                      <Trans message="Copied!" />
                    ) : (
                      <Trans message="Copy" />
                    )}
                  </Button>
                }
              />
              <div className="mt-6 text-xs text-muted">
                <Trans
                  message="Token expires at :time"
                  values={{time: <FormattedDate date={tokenData.expires_at} />}}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </AccountSettingsPanel>
  );
}
